import { Link, useForm } from "@inertiajs/react";
import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import DomainAddIntro, { DomainAddHeader } from "@/components/domains/DomainAddIntro";
import DomainHostnameField, { sanitizeHostnameInput } from "@/components/domains/DomainHostnameField";
import DomainRedirectDialog from "@/components/domains/DomainRedirectDialog";
import Layout from "@/components/Layout";

type NewProps = {
  form: {
    url: string;
  };
};

type AddPhase = "idle" | "checking" | "confirming" | "failed" | "starting";

type UrlCheckResult = {
  reachable?: boolean;
  error?: string;
  url?: string;
  hostname?: string;
  confirmation_required?: boolean;
};

const CHECKING_LABEL_DELAY_MS = 1000;
const DEFAULT_UNREACHABLE = "We couldn't reach that site. Check the URL or try again.";
const PROBING_HELP =
  "Explore any interaction, including forms and signups. Off: navigate and browse only, attempting to skip actions with side effects.";

export default function New({ form }: NewProps) {
  const initialHost = sanitizeHostnameInput(form.url);
  const { data, errors, processing, post, setData, clearErrors, transform } = useForm({
    url: initialHost,
    full_application_probing: false,
  });
  const [phase, setPhase] = useState<AddPhase>("idle");
  const [checkError, setCheckError] = useState("");
  const [showCheckingLabel, setShowCheckingLabel] = useState(false);
  const [pendingHostname, setPendingHostname] = useState<string | null>(null);
  const checkingLabelTimer = useRef<number | null>(null);
  const scanHostnameRef = useRef(initialHost);
  const probingRef = useRef(data.full_application_probing);
  const busy = phase === "checking" || phase === "starting" || processing;
  const fieldError = checkError || errors.url;
  const statusMessage = statusLine(phase, showCheckingLabel);

  probingRef.current = data.full_application_probing;

  transform(() => ({
    domain: {
      url: `https://${scanHostnameRef.current}`,
      full_application_probing: probingRef.current,
    },
  }));

  useEffect(() => {
    return () => {
      if (checkingLabelTimer.current !== null) {
        window.clearTimeout(checkingLabelTimer.current);
        checkingLabelTimer.current = null;
      }
    };
  }, []);

  function clearCheckingLabelTimer() {
    if (checkingLabelTimer.current !== null) {
      window.clearTimeout(checkingLabelTimer.current);
      checkingLabelTimer.current = null;
    }
  }

  async function validateAndStart(hostname: string) {
    setPhase("checking");
    setCheckError("");
    setPendingHostname(null);
    setShowCheckingLabel(false);
    clearErrors();
    clearCheckingLabelTimer();

    checkingLabelTimer.current = window.setTimeout(() => {
      setShowCheckingLabel(true);
      checkingLabelTimer.current = null;
    }, CHECKING_LABEL_DELAY_MS);

    try {
      const response = await fetch(`/domains/url-check?url=${encodeURIComponent(`https://${hostname}`)}`, {
        headers: { Accept: "application/json" },
      });
      const result = (await response.json()) as UrlCheckResult;

      clearCheckingLabelTimer();
      setShowCheckingLabel(false);

      if (!result.reachable || !result.hostname) {
        setPhase("failed");
        setCheckError(result.error || DEFAULT_UNREACHABLE);
        return;
      }

      if (result.confirmation_required) {
        setPendingHostname(result.hostname);
        setPhase("confirming");
        return;
      }

      startAudit(result.hostname);
    } catch {
      clearCheckingLabelTimer();
      setShowCheckingLabel(false);
      setPhase("failed");
      setCheckError(DEFAULT_UNREACHABLE);
    }
  }

  function startAudit(hostname: string) {
    setPendingHostname(null);
    scanHostnameRef.current = hostname;
    setData("url", hostname);
    setPhase("starting");
    post("/domains", {
      preserveState: true,
      onError: () => setPhase("idle"),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || phase === "confirming") return;

    void validateAndStart(data.url);
  }

  return (
    <Layout title="Add website">
      <div className="mx-auto max-w-2xl">
        <DomainAddHeader />

        <form onSubmit={handleSubmit} className="card">
          <div className="card-body">
            <DomainAddIntro />

            <div className="my-6 border-t border-gray-200" />

            {statusMessage ? (
              <p className="mb-4 text-sm text-gray-500" aria-live="polite">
                {statusMessage}
              </p>
            ) : null}

            <DomainHostnameField
              disabled={busy || phase === "confirming"}
              error={fieldError}
              label="Website domain"
              onChange={(hostname) => {
                setCheckError("");
                scanHostnameRef.current = hostname;
                setPhase((current) => (current === "failed" ? "idle" : current));
                setData("url", hostname);
              }}
              value={data.url}
            />

            <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3.5">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={data.full_application_probing}
                  disabled={busy || phase === "confirming"}
                  onChange={(event) => setData("full_application_probing", event.target.checked)}
                />
                <span>
                  <span className="block text-[0.95rem] font-semibold text-gray-900">
                    Allow full application probing
                  </span>
                  <span className="mt-1.5 block text-[0.8125rem] leading-relaxed text-gray-600">{PROBING_HELP}</span>
                </span>
              </label>
            </div>

            <div className="mt-8 flex flex-col-reverse items-stretch justify-between gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:items-center">
              <Link
                href="/domains"
                className={`text-sm font-medium text-primary hover:underline ${
                  busy || phase === "confirming" ? "pointer-events-none opacity-50" : ""
                }`}
                tabIndex={busy || phase === "confirming" ? -1 : undefined}
                aria-disabled={busy || phase === "confirming"}
              >
                Back to domains
              </Link>
              <button type="submit" className="btn btn-primary" disabled={busy || phase === "confirming"}>
                {buttonLabel(phase, processing, showCheckingLabel)}
              </button>
            </div>
          </div>
        </form>
      </div>

      <DomainRedirectDialog
        fromHostname={data.url}
        onCancel={() => {
          setPendingHostname(null);
          setPhase("idle");
        }}
        onConfirm={() => {
          if (!pendingHostname) return;
          startAudit(pendingHostname);
        }}
        open={phase === "confirming" && pendingHostname !== null}
        toHostname={pendingHostname ?? ""}
      />
    </Layout>
  );
}

function statusLine(phase: AddPhase, showCheckingLabel: boolean) {
  if (phase === "confirming") return "Confirm the redirected website to continue.";
  if (phase === "checking" && showCheckingLabel) {
    return "Checking that we can reach this website.";
  }
  if (phase === "starting") return "Adding your website.";
  return null;
}

function buttonLabel(phase: AddPhase, processing: boolean, showCheckingLabel: boolean) {
  if (phase === "confirming") return "Add website";
  if (phase === "checking" && showCheckingLabel) return "Checking website…";
  if (phase === "starting" || processing) return "Adding website…";
  return "Add website";
}
